<template>
  <v-card class="shadow-off border-on pa-4 d-flex flex-column gap-3">
    <!-- Count section: always visible, shows skeleton during loading -->
    <div class="d-flex align-center">
      <CommonCountCard
        data-testid="access-rights-table__count-card"
        :count="meta.totalCount || items.length"
        page-name="access_management"
        label-key="ru_entity_plural_label"
        :fallback-label="$nova.labelsName('access_management','ru_entity_plural_label','Reporting Units')"
        :border-color="'#2C2C35'"
        :background-color="'#F7F7F7'"
        :height="'44px'"
        showWhenZero
        :loading="loading"
      />

      <v-spacer />

      <v-tooltip left content-class="tooltip-left" v-if="selectedItems.length > 1">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" color="" @click="$emit('edit-data', {}, 'bulk', selectedItems)">
            <img src="/img/icons/EditIcon.svg" alt="Bulk Update Access Icon" />
          </v-btn>
        </template>
        <span> Bulk Update Access</span>
      </v-tooltip>
    </div>

    <!-- Loading skeleton: shown during any loading state (initial load, filter, pagination) -->
    <div v-show="loading">
      <lb-datatablenew
        ref="skeletonTable"
        data-testid="access-rights-table__loading-skeleton"
        :headers="headers"
        :items="shimmerItems"
        :loading="false"
        :disableFilters="true"
        :bypassFiltering="true"
        :enableslot="shimmerSlots"
        :hidePagination="true"
        multiselectkey="_id"
        :disableCheckbox="true"
        class="rounded-lg"
      >
        <template v-for="slot in shimmerSlots" v-slot:[slot]>
          <v-skeleton-loader :key="slot" class="my-1" type="text" />
        </template>
      </lb-datatablenew>
    </div>

    <!-- Data table: always mounted so internal filter state persists.
         Uses v-show to hide during loading but keep filter state intact. -->
    <div v-show="!loading">
      <lb-datatablenew
          ref="dataTable"
          class="access-rights-table__datatable"
          :headers="headers" 
          :items="items"
          :perpage="perpage"
          :paginationOverAll="true"
          :paginationOverAllinline="false"
          :enableslot="['action', 'submitterFew', 'approverFew', 'regional_approverFew', 'managerFew','sodEnabled', 'ru_details']"
          :loading="loading"
          :disableFilters="loading || filtersDisabled" 
          multiselectkey="_id" 
          :disableCheckbox="loading"
          :selecteditems="selectedItems" 
          @selectItem="$emit('update:selectedItems', $event)"
          :hideColumnSelector="true"
          :serverSidePagination="true"
          :serverTotalCount="meta.totalCount || 0"
          :currentPage="page"
          :bypassFiltering="true"
          @loadNextPage="$emit('loadNextPage', $event)"
          @update:filters="handleFilterUpdate"
          @filter-menu-open="$emit('filter-menu-open', $event)"
        >
        <template v-slot:ru_details="{ item }">
          <div>
            <div class="">{{ item.code }} - {{ item.name }}</div>
          </div>
        </template>
        <template v-slot:submitterFew="{ item }">
          <div class="avatar-stack">
            <v-tooltip bottom content-class="tooltip-bottom" v-for="(v, k) in item.submitterFew"
              :key="k" >
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  size="32"
                  class="stack-avatar"
                  :style="{ zIndex: 10 - k }"
                  v-bind="attrs"
                  v-on="on"
                >
                  <div class="capitalize code-font">{{ v.code }}</div>
                </v-avatar>
              </template>
              <span>{{ v.name }}</span>
            </v-tooltip>

            <!-- +More Avatar with Tooltip -->
            <v-tooltip bottom content-class="tooltip-bottom" v-if="item.__submitterplus > 0">
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  size="32"
                  class="stack-avatar more-avatar"
                  v-bind="attrs"
                  v-on="on"
                >
                  +{{ item.__submitterplus }}
                </v-avatar>
              </template>
              <div style="min-width: 120px; max-width: 250px;">
                <div v-for="(v, k) in item.submitter.slice(2)" :key="k" class="d-flex align-center mb-1">
                  <span>{{ v.name }}</span>
                </div>
              </div>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:approverFew="{ item }">
          <div class="avatar-stack">
            <v-tooltip
              bottom
              content-class="tooltip-bottom"
              v-for="(v, k) in item.approverFew"
              :key="k"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  size="32"
                  class="stack-avatar"
                  :style="{ zIndex: 10 - k }"
                  v-bind="attrs"
                  v-on="on"
                >
                  <div class="capitalize code-font">{{ v.code }}</div>
                </v-avatar>
              </template>
              <span>{{ v.name }}</span>
            </v-tooltip>
            <v-tooltip bottom content-class="tooltip-bottom" v-if="item.__approverplus > 0">
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  size="32"
                  class="stack-avatar more-avatar"
                  v-bind="attrs"
                  v-on="on"
                >
                  +{{ item.__approverplus }}
                </v-avatar>
              </template>
              <div style="min-width: 120px; max-width: 250px;">
                <div v-for="(v, k) in item.approver.slice(2)" :key="k" class="d-flex align-center mb-1">
                  <span>{{ v.name }}</span>
                </div>
              </div>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:regional_approverFew="{ item }">
          <div class="avatar-stack">
            <v-tooltip
              bottom
              content-class="tooltip-bottom"
              v-for="(v, k) in item.regional_approverFew"
              :key="k"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  size="32"
                  class="stack-avatar"
                  :style="{ zIndex: 10 - k }"
                  v-bind="attrs"
                  v-on="on"
                >
                  <div class="capitalize code-font">{{ v.code }}</div>
                </v-avatar>
              </template>
              <span>{{ v.name }}</span>
            </v-tooltip>
            <v-tooltip bottom content-class="tooltip-bottom" v-if="item.__regional_approverplus > 0">
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  size="32"
                  class="stack-avatar more-avatar"
                  v-bind="attrs"
                  v-on="on"
                >
                  +{{ item.__regional_approverplus }}
                </v-avatar>
              </template>
              <div style="min-width: 120px; max-width: 250px;">
                <div v-for="(v, k) in item.regional_approver.slice(2)" :key="k" class="d-flex align-center mb-1">
                  <span>{{ v.name }}</span>
                </div>
              </div>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:managerFew="{ item }">
          <div class="avatar-stack">
            <v-tooltip
              bottom
              content-class="tooltip-bottom"
              v-for="(v, k) in item.managerFew"
              :key="k"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  size="32"
                  class="stack-avatar"
                  :style="{ zIndex: 10 - k }"
                  v-bind="attrs"
                  v-on="on"
                >
                  <div class="capitalize code-font">{{ v.code }}</div>
                </v-avatar>
              </template>
              <span>{{ v.name }}</span>
            </v-tooltip>
            <v-tooltip bottom content-class="tooltip-bottom" v-if="item.__managerplus > 0">
              <template v-slot:activator="{ on, attrs }">
                <v-avatar
                  size="32"
                  class="stack-avatar more-avatar"
                  v-bind="attrs"
                  v-on="on"
                >
                  +{{ item.__managerplus }}
                </v-avatar>
              </template>
              <div style="min-width: 120px; max-width: 250px;">
                <div v-for="(v, k) in item.manager.slice(2)" :key="k" class="d-flex align-center mb-1">
                  <div class="capitalize code-font">{{ v.code }}</div>
                  <span>{{ v.name }}</span>
                </div>
              </div>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:sodEnabled="{ item }">
          <div class="d-flex justify-center">
            <StatusIndicator
              :variant="item.isSodEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
              :customLabel="item.sodEnabled"
              size="sm"
            />
          </div>
        </template>
        <template v-slot:action="{ item }">
          <v-tooltip left content-class="tooltip-left" v-if="$nova.hasRight(right, 'edit')">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" color="" @click="$emit('edit-data', item, 'single', item._id)">
                <img src="/img/icons/EditIcon.svg" alt="Edit Access Icon">
              </v-btn>
            </template>
            <span>Update for {{ $nova.labelsName('access_management','actual_label','Actual') }} Cycle</span>
          </v-tooltip>
        </template>
      </lb-datatablenew>
    </div>

    <!-- Empty State: only shown when there's genuinely no data AND no active filters -->
    <div v-if="!loading && !hasData && !hasActiveFilters" class="title grey--text pa-5 text-center">
      No Data Found
    </div>
  </v-card>
</template>

<script>
import StatusIndicator from '../../../components/StatusIndicator.vue';
import CommonCountCard from '@/components/CommonCountCard.vue';
import { STATUS_VARIANTS } from '../../../utils/statusConstants';
import { ITEMS_PER_PAGE } from '../../../utils/constants';

export default {
  name: 'AccessRightsTable',
  components: {
    StatusIndicator,
    CommonCountCard
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    },
    dataLength: {
      type: Number,
      default: 0
    },
    selectedItems: {
      type: Array,
      default: () => []
    },
    headers: {
      type: Array,
      required: true
    },
    right: {
      type: String,
      default: ''
    },
    page: {
      type: Number,
      default: 0
    },
    meta: {
      type: Object,
      default: () => ({})
    },
    perpage: {
      type: Number,
      default: ITEMS_PER_PAGE
    },
    hasActiveFilters: {
      type: Boolean,
      default: false
    },
    /**
     * When true, column filter buttons show a loading spinner (e.g. while hierarchy data is loading)
     */
    filtersDisabled: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      STATUS_VARIANTS,
      shimmerSlots: ['indexno', 'ru_details', 'submitterFew', 'approverFew', 'regional_approverFew', 'managerFew', 'sodEnabled', 'network', 'region', 'country', 'action'],
    }
  },
  computed: {
    hasData() {
      return this.dataLength > 0 || this.items.length > 0;
    },
    /**
     * Generates shimmer/skeleton placeholder items for loading state
     * @returns {Array} Array of placeholder items for skeleton loader display
     */
    shimmerItems() {
      return Array.from({ length: 15 }, (_, index) => ({
        _id: `shimmer-${index}`,
        indexno: '',
        ru_details: '',
        submitterFew: '',
        approverFew: '',
        regional_approverFew: '',
        managerFew: '',
        sodEnabled: '',
        network: '',
        region: '',
        country: '',
        action: '',
      }));
    },
  },
  methods: {
    handleFilterUpdate(filters) {
      this.$emit('update:filters', filters);
    },
    setExternalFilterItems(key, items) {
      if (this.$refs.dataTable) {
        this.$refs.dataTable.setExternalFilterItems(key, items);
      }
    },
    syncFilterStateToSkeleton() {
      const dataTable = this.$refs.dataTable;
      const skeletonTable = this.$refs.skeletonTable;
      if (dataTable && skeletonTable) {
        skeletonTable.selectedFilterItems = { ...dataTable.selectedFilterItems };
        skeletonTable.showFilter = [...dataTable.showFilter];
      }
    },
  },
  watch: {
    loading(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.syncFilterStateToSkeleton();
        });
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .access-rights-table__datatable [data-testid="data-table-new__toolbar"] {
  min-height: 2.5rem;
}

.avatar-stack {
  display: flex;
  position: relative;
  align-items: center;
  
  .stack-avatar {
    border: 2px solid white;
    transition: transform 0.2s ease;
    
    &:hover {
      transform: translateY(-2px);
      z-index: 20 !important;
    }
    
    // Add negative margin to create overlap
    &:not(:first-child) {
      margin-left: -12px;
    }
  }

  .more-avatar {
    margin-left: -12px;
    background-color: #f0f2f5;
    color: #666;
    font-size: 12px;
    font-weight: 500;
  }
}
.code-font {
  font-size: 12px;
}
</style>
